// src/contexts/GridsDataContext.tsx
//
// Single source of truth for LOADING grids. Previously GridContext,
// MultiGridContext and StabilizedContexts each ran their own
// `supabase.from('grids').select(...)` fetch and their own 'grids-changes'
// realtime subscription — three copies of the same data pipe. This provider owns
// ONE fetch and ONE subscription and exposes the raw grid rows; the three
// providers consume it and keep their own transform, API, mutations and
// active-grid state, so behaviour is unchanged.
//
// The select is a superset (includes the grid_types join MultiGridContext needs);
// the other consumers ignore the extra field via the shared transformGridRow.

import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { supabase, DB_SCHEMA } from '../supabase/config';

interface GridsDataContextType {
  rawGrids: any[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const GridsDataContext = createContext<GridsDataContextType | undefined>(undefined);

export const GridsDataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [rawGrids, setRawGrids] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const loadingRef = useRef(false);
  const reloadTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchGrids = useCallback(async () => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    try {
      const { data, error } = await supabase
        .from('grids')
        .select('*, grid_types!grids_grid_type_id_fkey(*)');
      if (error) {
        console.error('❌ GridsData: Error fetching grids:', error);
        setError(error.message);
        return;
      }
      setRawGrids(data || []);
      setError(null);
    } catch (e: any) {
      console.error('❌ GridsData: Error fetching grids:', e);
      setError(e?.message || 'Unknown error');
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, []);

  useEffect(() => {
    fetchGrids();

    // Single realtime subscription (debounced), matching the previous providers'
    // channel config. Schema stays 'public' to preserve existing behaviour.
    const debouncedReload = () => {
      if (reloadTimeout.current) clearTimeout(reloadTimeout.current);
      reloadTimeout.current = setTimeout(() => {
        fetchGrids();
      }, 100);
    };

    const subscription = supabase
      .channel('grids-data-shared')
      .on('postgres_changes', { event: '*', schema: DB_SCHEMA, table: 'grids' }, debouncedReload)
      .subscribe();

    return () => {
      if (reloadTimeout.current) clearTimeout(reloadTimeout.current);
      subscription.unsubscribe();
    };
  }, [fetchGrids]);

  return (
    <GridsDataContext.Provider value={{ rawGrids, loading, error, refetch: fetchGrids }}>
      {children}
    </GridsDataContext.Provider>
  );
};

export const useGridsData = (): GridsDataContextType => {
  const ctx = useContext(GridsDataContext);
  if (!ctx) {
    throw new Error('useGridsData must be used within a GridsDataProvider');
  }
  return ctx;
};
